import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Group, Spinner, Text, Center, IconButton } from '@chakra-ui/react';
import ResultsGrid from '../../../components/basic/ResultsGrid';
import DashboardSectionTile from '../../../components/core/DashboardSectionTile';
import './DataImportPreview.scss';
import { Tooltip } from '@/components/ui/tooltip';
import { IoInformationCircleOutline } from 'react-icons/io5';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '@/components/ui/dialog';
import { MdArrowBack, MdWarning } from 'react-icons/md';

// Coupled to the message text data_import_config's SQL templates return in `edited_data`
// for an unparseable date (see id 5/34/39/40 in the import config). If that wording or the
// field carrying it changes on the backend, this check needs to be updated too.
const INVALID_DATE_MESSAGE_PREFIX = 'Invalid date format';

const DataImportPreview = ({
  previewTableData,
  summaryTableData,
  onClickBackButton,
  onClickConfirmButton,
  onClickFinishButton,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const getPOsCount = () => {
    const rows = summaryTableData?.rows || [];
    return new Set(rows.map(row => row.po_id)).size;
  };

  const hasInvalidRows = (summaryTableData?.rows || []).some(
    row => typeof row.edited_data === 'string' && row.edited_data.startsWith(INVALID_DATE_MESSAGE_PREFIX)
  );

  return (
    <>
      <DashboardSectionTile
        title={
          <>
            Preview Upload&nbsp;
            <Tooltip
              content="Display a preview of the fields uploaded in the CSV or Excel file for verification"
              positioning={{ placement: 'right-end' }}
            >
              <IconButton
                width="14px"
                height="14px"
                padding="0"
                minW="auto"
                borderRadius="50%"
                color="#878787"
                variant="unstyled"
              >
                <IoInformationCircleOutline size="14px" />
              </IconButton>
            </Tooltip>
          </>
        }
        padding="22px 16px 49px 15px"
      >
        {previewTableData && (
          <Box marginLeft="10px !important">
            <ResultsGrid
              id="DataImportPreviewUpload"
              className="data_import_preview_results_grid"
              {...previewTableData}
              minHeight={57}
              rowHeight={() => 57}
            />
            <Text fontWeight="700" fontSize="12px" marginTop="23px">
              Total Records: {previewTableData?.rows?.length || 0}
            </Text>
          </Box>
        )}
      </DashboardSectionTile>
      <DashboardSectionTile
        title={
          <>
            Summary&nbsp;
            <Tooltip
              content="Show a summary of changes, including which fields need to be updated from old values to new values"
              positioning={{ placement: 'right-end' }}
            >
              <IconButton
                width="14px"
                height="14px"
                padding="0"
                minW="auto"
                borderRadius="50%"
                color="#878787"
                variant="unstyled"
              >
                <IoInformationCircleOutline size="14px" />
              </IconButton>
            </Tooltip>
          </>
        }
        padding="22px 16px 49px 15px"
        marginTop="55px"
      >
        {summaryTableData && (
          <Box marginLeft="10px !important">
            <ResultsGrid
              id="DataImportPreviewSummary"
              className="data_import_preview_results_grid"
              {...summaryTableData}
              minHeight={57}
              rowHeight={() => 57}
            />
            {hasInvalidRows && (
              <Text color="red.500" fontWeight="700" fontSize="14px" marginTop="16px">
                <MdWarning /> Invalid date format detected. Correct the source file and re-upload.
              </Text>
            )}
          </Box>
        )}
        {!summaryTableData && (
          <Center>
            <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="actionSecondary.default" size="xl" />
          </Center>
        )}
      </DashboardSectionTile>
      <Group
        colorScheme="actionPrimary"
        justifyContent="flex-end"
        width="100%"
        marginTop="39px"
        fontSize="16px"
        gap="15px"
      >
        {onClickBackButton && (
          <Button variant="outline" width="117px" onClick={onClickBackButton} fontSize="inherit" fontWeight="600">
            <MdArrowBack />
            Back
          </Button>
        )}
        {onClickConfirmButton && (
          <Button
            width="110px"
            disabled={!summaryTableData?.rows?.length || hasInvalidRows}
            fontSize="inherit"
            fontWeight="900"
            onClick={onOpen}
          >
            Confirm
          </Button>
        )}
        {onClickFinishButton && (
          <Button width="110px" onClick={onClickFinishButton} fontSize="inherit" fontWeight="900">
            Finish
          </Button>
        )}
      </Group>
      {summaryTableData && previewTableData ? (
        <DialogRoot open={isOpen} onClose={onClose} centered>
          <DialogContent minWidth="600px">
            <DialogHeader fontSize="20px" borderBottomWidth="1px">
              <MdWarning color="#545D69" /> &nbsp;Do you wish to continue?
            </DialogHeader>
            <DialogCloseTrigger asChild top="12px" onClick={onClose} />
            <DialogBody>
              <Center minHeight="264px">
                <Text fontSize="18px">
                  You are about to make <b>{summaryTableData?.rows?.length || 0} changes</b> to{' '}
                  <b>{getPOsCount()} PO&rsquo;s.</b>
                </Text>
              </Center>
            </DialogBody>
            <DialogFooter borderTopWidth="1px">
              <Group colorScheme="actionPrimary" marginLeft="auto" fontSize="16px" fontWeight="900" gap="9px">
                <Button
                  variant="outline"
                  fontSize="inherit"
                  fontWeight="inherit"
                  height="40px"
                  width="130px"
                  onClick={onClose}
                  backgroundColor="gray.200"
                  color="#333"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                    onClickConfirmButton();
                  }}
                  fontSize="inherit"
                  fontWeight="inherit"
                  height="40px"
                  width="126px"
                >
                  Continue
                </Button>
              </Group>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      ) : (
        ''
      )}
    </>
  );
};

DataImportPreview.propTypes = {
  previewTableData: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.object),
  }),
  summaryTableData: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.object),
  }),
  onClickBackButton: PropTypes.func,
  onClickConfirmButton: PropTypes.func,
  onClickFinishButton: PropTypes.func,
};

export default DataImportPreview;
